"use client"

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import type { ChatMessage } from "@/lib/chat/map-api-message"
import {
  applyServerReactionAggregate,
  mergeReactionsFromMessages,
  toggleViewerReaction,
  toReactionCounts,
  type ChatMessageReactionsMap,
  type ViewerReactionsMap,
} from "@/lib/chat/message-reactions-ui"
import { resolveMessageReportPayload, type MessageReportPayload } from "@/lib/chat/message-report"
import { isCopyableMessage, toReplyPreview, type ChatReplyPreview, type ReplySenderContext } from "@/lib/chat/reply-preview"
import { setChatMessageReaction } from "@/lib/chat/set-message-reaction"

export type MessageAnchorRect = {
  x: number
  y: number
  width: number
  height: number
}

type MessageReportContext = {
  isOwnMessage: boolean
  reportedUsername?: string | null
  reportedAvatarUrl?: string | null
  itemTitle?: string
}

type OverlayLayoutContext = {
  showAvatar: boolean
  showTail: boolean
  replyPreview?: ChatReplyPreview | null
}

type OverlayTarget = {
  messageKey: string
  message: ChatMessage
  ctx: MessageReportContext
  anchor: MessageAnchorRect
  layout: OverlayLayoutContext
}

type InteractionState =
  | { kind: "idle" }
  | { kind: "overlay"; target: OverlayTarget }
  | { kind: "reply"; replyTo: ChatMessage }

type InteractionAction =
  | { type: "open_overlay"; target: OverlayTarget }
  | { type: "dismiss_overlay" }
  | { type: "start_reply"; replyTo: ChatMessage }
  | { type: "clear_reply" }
  | { type: "reset" }

function interactionReducer(state: InteractionState, action: InteractionAction): InteractionState {
  switch (action.type) {
    case "open_overlay":
      return { kind: "overlay", target: action.target }
    case "dismiss_overlay":
      return state.kind === "reply" ? state : { kind: "idle" }
    case "start_reply":
      return { kind: "reply", replyTo: action.replyTo }
    case "clear_reply":
      return { kind: "idle" }
    case "reset":
      return { kind: "idle" }
    default:
      return state
  }
}

export function useChatMessageInteractions(replySenderContext?: ReplySenderContext) {
  const [state, dispatch] = useReducer(interactionReducer, { kind: "idle" })
  const [reportOpen, setReportOpen] = useState(false)
  const [reportTarget, setReportTarget] = useState<MessageReportPayload | null>(null)
  const [reactionCounts, setReactionCounts] = useState<ChatMessageReactionsMap>({})
  const [viewerReactions, setViewerReactions] = useState<ViewerReactionsMap>({})
  const reactionCountsRef = useRef(reactionCounts)
  const viewerReactionsRef = useRef(viewerReactions)

  useEffect(() => {
    reactionCountsRef.current = reactionCounts
  }, [reactionCounts])

  useEffect(() => {
    viewerReactionsRef.current = viewerReactions
  }, [viewerReactions])

  const selectedMessageKey = state.kind === "overlay" ? state.target.messageKey : null
  const replyToMessage = state.kind === "reply" ? state.replyTo : null
  const overlayTarget = state.kind === "overlay" ? state.target : null

  const resetInteractions = useCallback(() => {
    dispatch({ type: "reset" })
  }, [])

  const seedReactionsFromMessages = useCallback((messages: ChatMessage[]) => {
    const { counts, viewer, changed } = mergeReactionsFromMessages(
      reactionCountsRef.current,
      viewerReactionsRef.current,
      messages,
    )
    if (!changed) return
    reactionCountsRef.current = counts
    viewerReactionsRef.current = viewer
    setReactionCounts(counts)
    setViewerReactions(viewer)
  }, [])

  const openReport = useCallback((payload: MessageReportPayload) => {
    dispatch({ type: "dismiss_overlay" })
    setReportTarget(payload)
    setReportOpen(true)
  }, [])

  const closeReport = useCallback(() => {
    setReportOpen(false)
    setReportTarget(null)
    resetInteractions()
  }, [resetInteractions])

  const buildPayload = useCallback(
    (message: ChatMessage, ctx: MessageReportContext) => resolveMessageReportPayload(message, ctx),
    [],
  )

  const openMessageOverlay = useCallback(
    (
      message: ChatMessage,
      ctx: MessageReportContext,
      messageKey: string,
      anchor: MessageAnchorRect,
      layout: OverlayLayoutContext,
    ) => {
      dispatch({
        type: "open_overlay",
        target: { messageKey, message, ctx, anchor, layout },
      })
    },
    [],
  )

  const dismissOverlay = useCallback(() => {
    dispatch({ type: "dismiss_overlay" })
  }, [])

  const startReply = useCallback((message: ChatMessage) => {
    dispatch({ type: "start_reply", replyTo: message })
  }, [])

  const clearReply = useCallback(() => {
    dispatch({ type: "clear_reply" })
  }, [])

  const handleOverlayReply = useCallback(() => {
    if (!overlayTarget) return
    startReply(overlayTarget.message)
  }, [overlayTarget, startReply])

  const handleOverlayReport = useCallback(() => {
    if (!overlayTarget) return
    const payload = resolveMessageReportPayload(overlayTarget.message, overlayTarget.ctx)
    if (!payload) {
      dismissOverlay()
      return
    }
    openReport(payload)
  }, [dismissOverlay, openReport, overlayTarget])

  const handleOverlayCopy = useCallback(async () => {
    if (!overlayTarget || !isCopyableMessage(overlayTarget.message)) return
    try {
      await navigator.clipboard.writeText((overlayTarget.message.content ?? "").trim())
    } catch {}
    dismissOverlay()
  }, [dismissOverlay, overlayTarget])

  const applyReaction = useCallback(
    (messageId: string, emoji: string) => {
      const previousCounts = reactionCountsRef.current
      const previousViewer = viewerReactionsRef.current
      const optimistic = toggleViewerReaction(previousCounts, previousViewer, messageId, emoji)
      reactionCountsRef.current = optimistic.counts
      viewerReactionsRef.current = optimistic.viewer
      setReactionCounts(optimistic.counts)
      setViewerReactions(optimistic.viewer)
      dismissOverlay()

      void setChatMessageReaction({ messageId, emoji })
        .then((result) => {
          const synced = applyServerReactionAggregate(
            reactionCountsRef.current,
            viewerReactionsRef.current,
            messageId,
            result.counts,
            result.viewerEmoji,
          )
          reactionCountsRef.current = synced.counts
          viewerReactionsRef.current = synced.viewer
          setReactionCounts(synced.counts)
          setViewerReactions(synced.viewer)
        })
        .catch(() => {
          reactionCountsRef.current = previousCounts
          viewerReactionsRef.current = previousViewer
          setReactionCounts(previousCounts)
          setViewerReactions(previousViewer)
        })
    },
    [dismissOverlay],
  )

  const handleReaction = useCallback(
    (emoji: string) => {
      if (!overlayTarget?.message.id) return
      applyReaction(overlayTarget.message.id, emoji)
    },
    [applyReaction, overlayTarget],
  )

  const getMessageReactions = useCallback(
    (message: ChatMessage) => {
      if (!message.id) return undefined
      if (Object.prototype.hasOwnProperty.call(reactionCounts, message.id)) {
        const local = reactionCounts[message.id]
        return local && Object.keys(local).length > 0 ? local : undefined
      }
      return toReactionCounts(message.reactions ?? undefined)
    },
    [reactionCounts],
  )

  const getViewerReaction = useCallback(
    (message: ChatMessage) => {
      if (!message.id) return message.viewerReaction ?? null
      if (Object.prototype.hasOwnProperty.call(viewerReactions, message.id)) {
        return viewerReactions[message.id] ?? null
      }
      return message.viewerReaction ?? null
    },
    [viewerReactions],
  )

  const resolveReplyPreview = useCallback((message: ChatMessage) => {
    if (message.replyTo) {
      return { ...message.replyTo, listing: message.listing ?? null, order: message.order ?? null }
    }
    return null
  }, [])

  const replyPreview = useMemo(
    () => (replyToMessage ? toReplyPreview(replyToMessage, replySenderContext) : null),
    [replySenderContext, replyToMessage],
  )

  return {
    selectedMessageKey,
    overlayTarget,
    replyToMessage,
    replyPreview,
    reactionCounts,
    getMessageReactions,
    getViewerReaction,
    seedReactionsFromMessages,
    messageReportOpen: reportOpen,
    messageReportTarget: reportTarget,
    closeMessageReport: closeReport,
    buildMessageReportPayload: buildPayload,
    openMessageOverlay,
    dismissOverlay,
    startReply,
    clearReply,
    resetInteractions,
    handleOverlayReply,
    handleOverlayReport,
    handleOverlayCopy,
    handleReaction,
    applyReaction,
    resolveReplyPreview,
  }
}
